import { notEmpty } from '@talos/kyoko';
import { useMemo } from 'react';
import type { LayoutSpecResponse } from '../components/AppLayout/LayoutSpecs/LayoutSpecSlice';
import { type SpaceVisibility, useLayoutSpecsQuery } from '../components/AppLayout/LayoutSpecs/LayoutSpecSlice';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import type { LayoutSection } from '../types/LayoutConfig';

export function useDefaultSpaces(
  layoutSection: LayoutSection,
  availability: SpaceVisibility
): LayoutSpecResponse[] | undefined {
  const { data: sharedLayoutSpecs, isLoading } = useLayoutSpecsQuery();
  const { layoutConfigs } = useAppLayoutConfig(layoutSection);

  return useMemo(() => {
    // Include both id and templateId so that a server template whose templateId matches
    // an existing layout's id (e.g. the local default layout with id='default' but no templateId)
    // is not re-added as a duplicate.
    const existingTemplateIds = new Set(
      (layoutConfigs ?? []).flatMap(spec => [spec.id, spec.templateId]).filter(notEmpty)
    );

    const filteredSpecs =
      sharedLayoutSpecs?.filter(
        spec =>
          spec.Config.section === layoutSection &&
          spec.Config.availability === availability &&
          spec.Config.layoutSpec.templateId &&
          !existingTemplateIds.has(spec.Config.layoutSpec.templateId)
      ) ?? [];

    return isLoading ? undefined : filteredSpecs;
  }, [sharedLayoutSpecs, layoutSection, layoutConfigs, availability, isLoading]);
}
